<template>
  <div class="slider-container">
    <div class="slider-top">
      <Transition name="fade">
        <p v-if="!isStarted" class="label-salary-scale">
          {{ d['Salary.Result.SalaryScaleLabel']?.item2 }}
        </p>
      </Transition>
      <div class="slider-info" v-html="displayText" :class="{ visible: isStarted }"></div>
      <!-- Salary Details -->
      <div
        class="salary-details-container"
        ref="salaryDetailsContainer"
        :class="{ visible: isStarted }"
      >
        <div class="salary-details" v-for="(q, index) in QUARTILE_MAP" :key="q.key">
          <div class="salary">
            <span class="value"
              >{{ filteredData[q.key] }} {{ d['Salary.Result.LabelCurrency']?.item2 }}</span
            >
            <span class="post-fix">
              {{ d['Salary.Result.SalaryTotalPostfix']?.item2 }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Slider Track -->
    <div
      class="slider-track"
      ref="track"
      @mousedown="onTrackClick"
      @touchstart="onTrackClick"
      @click="onTrackClick"
    >
      <!-- Slider Steps -->
      <div
        v-for="q in QUARTILE_MAP"
        :key="q.step"
        :class="`slider-step-${q.step}`"
        :style="{ left: '0', right: `${100 - q.step}%` }"
      ></div>

      <!-- Slider Thumb -->
      <div
        class="slider-thumb"
        :style="{ left: `${thumbPosition}%` }"
        @mousedown.stop.prevent="startDragging"
        @touchstart.stop.prevent="startDragging"
      ></div>
    </div>

    <!-- Labels -->
    <div class="labels">
      <span v-for="(q, index) in QUARTILE_MAP" :key="q.key" :style="{ left: `${q.step}%` }">
        {{ filteredData[q.key] }} {{ d['Salary.Result.LabelCurrency']?.item2 }}
      </span>
    </div>
  </div>
</template>

<script>
import { state, localDictionary } from '@/lib/state'
import { QUARTILE_MAP } from '@/lib/mapping'
import { highlightText } from '@/util/stringUtil'

export default {
  props: {
    steps: {
      type: Array,
      default: () => QUARTILE_MAP.map((q) => q.step),
    },
  },
  data() {
    return {
      thumbPosition: 50, // Thumb position in percentage
      currentStepIndex: state.salarySliderIndex,
      isDragging: false, // Dragging state
      trackRect: null, // Track dimensions cache
      d: localDictionary(),
      QUARTILE_MAP, // Make available in template
    }
  },
  computed: {
    filteredData() {
      if (!state.resultData) {
        return Object.fromEntries(QUARTILE_MAP.map((q) => [q.key, '']))
      }
      const format = new Intl.NumberFormat(state.culture)
      return Object.fromEntries(
        QUARTILE_MAP.map((q) => [
          q.key,
          state.resultData[q.key] != null ? format.format(state.resultData[q.key]) : '',
        ]),
      )
    },

    mappedSteps() {
      // Map step (25, 50, 75, 90) to formatted value
      return Object.fromEntries(QUARTILE_MAP.map((q) => [q.step, this.filteredData[q.key]]))
    },

    currentQuartile() {
      return QUARTILE_MAP[this.currentStepIndex]?.key || 'q2'
    },

    currentStep() {
      return QUARTILE_MAP[this.currentStepIndex]?.step || 50
    },

    currentQ() {
      // For legacy label lookup
      return `${this.currentStepIndex + 1}`
    },

    displayText() {
      const key = 'salarySliderLabelQ' + this.currentQ
      const text = state[key]
      if (!text || typeof text !== 'string') {
        return ''
      }
      return highlightText(text)
    },

    isStarted() {
      return state.isSalarySliderStarted
    },
  },
  watch: {
    currentStepIndex(newIndex) {
      state.salarySliderIndex = newIndex
      this.scrollToStep(newIndex)
    },
  },
  methods: {
    cacheTrackDimensions() {
      this.trackRect = this.$refs.track.getBoundingClientRect()
    },
    onTrackClick(event) {
      if (!this.trackRect) this.cacheTrackDimensions()
      const clickX = (event.clientX || event.touches[0].clientX) - this.trackRect.left
      const percentage = Math.min(100, Math.max(0, (clickX / this.trackRect.width) * 100))
      this.updateSlider(this.findClosestStep(percentage))
    },
    startDragging() {
      if (!this.trackRect) this.cacheTrackDimensions()
      let animationFrameId

      const onMove = (moveEvent) => {
        if (animationFrameId) return
        animationFrameId = requestAnimationFrame(() => {
          const clientX = moveEvent.clientX || moveEvent.touches[0].clientX
          const dragX = clientX - this.trackRect.left
          const percentage = Math.min(100, Math.max(0, (dragX / this.trackRect.width) * 100))
          this.thumbPosition = percentage
          animationFrameId = null
        })
      }

      const onEnd = () => {
        this.isDragging = false
        this.updateSlider(this.findClosestStep(this.thumbPosition))
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onEnd)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend', onEnd)
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId)
        }
      }

      this.isDragging = true
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onEnd)
      document.addEventListener('touchmove', onMove)
      document.addEventListener('touchend', onEnd)
    },
    findClosestStep(percentage) {
      return QUARTILE_MAP.reduce(
        (closestIndex, q, index) =>
          Math.abs(percentage - q.step) < Math.abs(percentage - QUARTILE_MAP[closestIndex].step)
            ? index
            : closestIndex,
        0,
      )
    },
    updateSlider(index) {
      if (!this.isStarted) state.isSalarySliderStarted = true
      this.currentStepIndex = index
      this.thumbPosition = QUARTILE_MAP[index].step
    },
    scrollToStep(index, smooth = true) {
      const salaryDetailsContainer = this.$refs.salaryDetailsContainer
      const salaryDetail = salaryDetailsContainer.children[index]
      salaryDetail.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'nearest',
        inline: 'center',
      })
    },
  },
  mounted() {
    this.cacheTrackDimensions()
    window.addEventListener('resize', this.cacheTrackDimensions)
    this.scrollToStep(this.currentStepIndex, false)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.cacheTrackDimensions)
  },
}
</script>

<style scoped lang="postcss">
.slider-top {
  position: relative;

  .label-salary-scale {
    font-size: var(--font-size-body);
    font-weight: var(--font-weight-bold);
    font-family: var(--font-it);
    text-transform: uppercase;
    text-align: center;
    position: absolute;
    width: 100%;
    top: 50%;
    transform: translateY(-50%);

    @media (min-width: 1024px) {
      font-size: 20px;
    }
  }

  .slider-info {
    font-family: var(--font-it-ex);
    font-size: var(--font-size-body);
    font-style: normal;
    font-weight: var(--font-weight-medium);
    text-align: center;
    opacity: 0;
    transition: opacity var(--ani-speed-7) var(--cubic-ease-in-out);
    padding-bottom: 20px;

    &.visible {
      opacity: 1;
    }

    @media (min-width: 1024px) {
      text-align: left;
    }
  }

  .salary-details-container {
    display: flex;
    overflow-x: scroll;
    scrollbar-width: none;
    opacity: 0;
    transition: opacity var(--ani-speed-7) var(--cubic-ease-in-out);

    &.visible {
      opacity: 1;
    }

    .salary-details {
      width: 100%;
      display: flex;

      .salary {
        padding-bottom: 20px;
        align-items: center;

        @media (min-width: 1024px) {
          column-gap: 10px;
          flex-direction: row;
          align-items: baseline;
        }

        .value {
          font-size: 50px;
          font-style: normal;
          font-weight: var(--font-weight-medium);
          line-height: 1;
          text-transform: uppercase;
        }

        .post-fix {
          font-size: var(--font-size-small);
          line-height: 1;
          font-weight: var(--font-weight-normal);
          font-family: var(--font-it-ex);
        }
      }
    }

    div {
      position: relative;
      width: 400px;
      display: flex;
      flex-direction: column;
      align-items: center;

      @media (min-width: 1024px) {
        width: 100%;
        flex: 0 0 100%;
        align-items: flex-start;
      }
    }
  }
}

/* Slider Container */
.slider-container {
  z-index: 0;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  position: relative;
  will-change: transform;
  --color-step-1: #4e6092;
  --color-step-2: #6877a1;
  --color-step-3: #8b96b6;
  --color-step-4: #b9bfd1;
  --color-step-5: #dadce4;

  @media (min-width: 1024px) {
    min-width: 100%;
  }
}

[class^='slider-step-'] {
  position: absolute;
  top: 0;
  bottom: 0;
}

.slider-step-25 {
  background: var(--color-step-1);
  z-index: 4;
}

.slider-step-50 {
  background: var(--color-step-2);
  z-index: 3;
}

.slider-step-75 {
  background: var(--color-step-3);
  z-index: 2;
}

.slider-step-90 {
  background: var(--color-step-4);
  z-index: 1;
}

/* Slider Track */
.slider-track {
  position: relative;
  width: 100%;
  height: 33px;
  background: var(--color-step-5);
  border-radius: 999px;
  overflow: hidden;
  cursor: pointer;

  @media (min-width: 1024px) {
    height: 21px;
  }
}

/* Thumb (draggable part) */
.slider-thumb {
  position: absolute;
  top: 2px;
  width: 28px;
  height: 28px;
  background: var(--color-white);
  border-radius: 50%;
  transform: translateX(-50%);
  cursor: grab;
  z-index: 50;

  @media (min-width: 1024px) {
    height: 18px;
    width: 18px;
  }
}

.slider-thumb:active {
  cursor: grabbing;
}

/* Labels */
.labels {
  position: relative;
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  height: 20px;
}

.labels span {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: var(--font-size-small);
  color: #afacac;
  word-break: keep-all;
  width: max-content;

  &:first-of-type {
    padding-right: 8%;
  }

  &:last-of-type {
    padding-left: 8%;
  }

  @media (min-width: 1024px) {
    &:first-of-type {
      padding-right: 8%;
    }

    &:last-of-type {
      padding-left: 8%;
    }
  }
}

/* Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--ani-speed-3) var(--cubic-ease-in-out);
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
